import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ButtonPublish from '../../../../components/_button/ButtonPublish';
import DropshippingProductTableItem from './DropshippingProductTableItem';
import {
  IProductQueryData,
  IProductVars,
  useProductQuery,
} from '../../../../../graphql/query/product/useProductQuery';
import useNotification from '../../../../components/_hook/useNotification';
import ButtonUnpublish from '../../../../components/_button/ButtonUnpublish';
import GraphQLTable from '../../../../components/_table/GraphQLTable';
import { useUnpublishProductsMutation } from '../../../../../graphql/mutation/product/unpublishProductsMutation';
import { usePublishProductsMutation } from '../../../../../graphql/mutation/product/publishProductsMutation';
import Helmet from '../../../../components/Helmet';
import { IProductSchema } from '../../../../../graphql/type/product';
import ProductLayout from '../../../../layout/Product';
import Context from '../../../../context';
import ButtonDisallowDropshipping from '../../_module/ButtonDisallowDropshipping';
import ButtonAllowDropshipping from '../../_module/ButtonAllowDropshipping';

function DropshippingProductTable() {
  const { t } = useTranslation();
  const { notification } = useNotification();
  const { company } = useContext(Context);
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [tableRefreshKey, setTableRefreshKey] = useState<number>(0);
  const [selectedItems, setSelectedItems] = useState<IProductSchema[]>([]);

  const [publishProductsMutation] = usePublishProductsMutation(
    `updatedProducts {
				_id
				status
			}`,
    {
      onCompleted: () => {
        notification.success(
          t('__item___updated', {
            item: `${selectedItems.length} ${t('product', {
              count: selectedItems.length,
            })}`,
          })
        );
        setSelectedItems([]);
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [unpublishProductsMutation] = useUnpublishProductsMutation(
    `updatedProducts {
				_id
				status
			}`,
    {
      onCompleted: () => {
        notification.success(
          t('__item___updated', {
            item: `${selectedItems.length} ${t('product', {
              count: selectedItems.length,
            })}`,
          })
        );
        setSelectedItems([]);
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  async function onClickPublish() {
    await publishProductsMutation({
      variables: { _ids: selectedItems.map((item) => item._id) },
    });
  }

  async function onClickUnpublish() {
    await unpublishProductsMutation({
      variables: { _ids: selectedItems.map((item) => item._id) },
    });
  }

  return (
    <>
      <Helmet
        title={`${t('dropshipping_product', { count: 2 })} | ${t('product')}`}
      />
      <Grid container justifyContent="space-between">
        <Button
          color="primary"
          variant="contained"
          onClick={() => setIsAddingProduct(!isAddingProduct)}
          startIcon={isAddingProduct ? <ArrowBackIosIcon /> : <AddIcon />}
        >
          {isAddingProduct ? t('back') : t('add_product')}
        </Button>
      </Grid>
      <GraphQLTable<IProductQueryData, IProductSchema, IProductVars>
        refreshKey={tableRefreshKey}
        select={`
              _id
              ordering
              company
              companyData {
                  _id
                  origin
                  slug
                  aka {
                      la
                      text
                  }
                  name {
                      la
                      text
                  }
              }
              name {
                  la
                  text
              }
              slug
              status 
              featured 
              prices {
                  final
                  currency
              }
              media {
                  images {
                      status 
                      filename
                      filename_small
      
                      filenameUrl
                      filenameSmallUrl
                  }
              }
              dropshipping {
                enable
              }
              updated {
                  time 
              }
              created {
                  time 
              }
       `}
        useQuery={useProductQuery}
        renderHeader={[
          {
            field: '_id',
            label: t('id'),
            align: 'left',
            sortable: true,
          },
          {
            field: 'name',
            label: t('name'),
            align: 'left',
          },
          {
            field: 'image',
            label: t('image'),
            align: 'left',
          },
          {
            field: 'finalPrice',
            label: t('price'),
            align: 'left',
            sortable: true,
          },
          {
            field: 'createdTime',
            label: t('created'),
            align: 'left',
            sortable: true,
          },
          {
            field: 'action',
            label: t('action'),
            align: 'right',
          },
        ]}
        renderItem={(item) => (
          <DropshippingProductTableItem
            item={item}
            setTableRefreshKey={setTableRefreshKey}
          />
        )}
        useCheckbox
        onItemSelected={setSelectedItems}
        selectedItems={selectedItems}
        defaultFilterOptions={{
          companyId: company._id,
          isDropshippingEnable: isAddingProduct ? undefined : true,
        }}
        searchFields={{
          _id: { label: t('id') },
          name: { label: t('name') },
        }}
        title={
          isAddingProduct
            ? t('add_dropshipping_product')
            : t('dropshipping_product')
        }
        defaultSort="createdTime"
        defaultSortOrder={-1}
        actionButtons={[
          <ButtonPublish
            key="publish"
            variant="contained"
            color="primary"
            size="small"
            text={t('publish')}
            onClick={onClickPublish}
          />,
          <ButtonUnpublish
            key="unpublish"
            color="primary"
            variant="contained"
            size="small"
            text={t('unpublish')}
            onClick={onClickUnpublish}
          />,
          ...(company.settings && company.settings.enableDropshipping
            ? [
                <ButtonAllowDropshipping
                  key="allowDropshipping"
                  products={selectedItems}
                  onCompleted={() => setSelectedItems([])}
                />,
                <ButtonDisallowDropshipping
                  key="disallowDropshipping"
                  products={selectedItems}
                  onCompleted={() => setSelectedItems([])}
                />,
              ]
            : []),
        ]}
        itemKey="_id"
      />
    </>
  );
}

export default () => <ProductLayout main={<DropshippingProductTable />} />;
